import { LinearProgressProps } from '@mui/material/LinearProgress';

import styled from '@emotion/styled';
import { useEffect, useMemo, useState } from 'react';
import { TextVariantProps, Theme } from './types';

const Container = styled.div`
    position: relative;
    width: 100%;
`;

export interface BaseProgressBarProps {
    duration: number;
    startedAt: number;
    calcFormula: (duration: number) => number;
    updateIntervalMS?: number;
    theme: Theme;
    ProgressText: React.FC<TextVariantProps>;
    ProgressBar: React.FC<LinearProgressProps>;
    minProgressShown?: number;
    maxProgressShown?: number;
}

const calculateElapsedTimeInSeconds = (startedAt: number) => (Date.now() - startedAt) / 1000;

const minMaxNormalize = ({
    value,
    oldMin,
    oldMax,
    newMin,
    newMax,
}: {
    value: number;
    oldMin: number;
    oldMax: number;
    newMin: number;
    newMax: number;
}) => ((value - oldMin) / (oldMax - oldMin)) * (newMax - newMin) + newMin;

const DEFAULT_UPDATE_INTERVAL = 10 * 1000;

export const BaseProgressBar = ({
    duration,
    startedAt,
    calcFormula,
    updateIntervalMS = DEFAULT_UPDATE_INTERVAL,
    theme,
    ProgressText,
    minProgressShown = 0,
    maxProgressShown = 99,
    ProgressBar,
}: BaseProgressBarProps) => {
    const processingTime = useMemo(() => calcFormula(duration), [duration]);
    const [elapsedSeconds, setElapsedSeconds] = useState(calculateElapsedTimeInSeconds(startedAt));

    const progress = Math.min(1, elapsedSeconds / processingTime);
    const normalizedProgress = Math.round(
        minMaxNormalize({
            value: progress,
            oldMin: 0,
            oldMax: 1,
            newMin: minProgressShown,
            newMax: maxProgressShown,
        })
    );
    const remainingSeconds = Math.max(0, Math.ceil(processingTime - elapsedSeconds));

    useEffect(() => {
        const interval = setInterval(() => {
            const newElapsedSeconds = calculateElapsedTimeInSeconds(startedAt);
            setElapsedSeconds(newElapsedSeconds);
        }, updateIntervalMS);

        return () => clearInterval(interval);
    }, [startedAt]);

    return (
        <Container>
            <ProgressBar variant="determinate" value={normalizedProgress} />
            <ProgressText progress={normalizedProgress} remainingSeconds={remainingSeconds} theme={theme} />
        </Container>
    );
};
